import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import VODFooter from '../components/VODFooter';

const VODTermsOfService = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Helmet>
        <title>Terms of Service | LTV VOD - Video Streaming Platform</title>
        <meta name="description" content="Terms of Service for LTV VOD streaming platform. Read our terms for video streaming, subscriptions, content licensing, and user responsibilities." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://ltvproduction.com/vod/terms-of-service" />
      </Helmet>

      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            Back
          </button>
          <Link to="/vod" className="flex items-center gap-2">
            <img src="/ltv-icon.png" alt="LTV VOD" className="h-8 w-8 brightness-0 invert" />
            <span className="font-bold">LTV VOD</span>
          </Link>
        </div>
      </header>
      
      {/* Content */}
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <h1 className="text-4xl font-bold mb-4">Terms of Service</h1>
          <p className="text-gray-400 mb-2">LTV VOD - Video On Demand Platform</p>
          <p className="text-gray-500 mb-8">Last updated: March 2026</p>
          
          <div className="space-y-8 text-gray-300">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Agreement to Terms</h2>
              <p className="mb-4">
                By accessing or using LTV VOD (the "Service"), operated by LTV Production LTD ("Company," "we," "us," or "our"), you agree to be bound by these Terms of Service ("Terms").
              </p>
              <p>
                If you do not agree to these Terms, you must not access or use our Service. These Terms apply to all visitors, users, and subscribers of LTV VOD.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. Description of Service</h2>
              <p className="mb-4">LTV VOD is a video-on-demand streaming platform that provides:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Access to video content including shows, episodes, films, and short-form content (Reels)</li>
                <li>Live streaming events and premieres</li>
                <li>Subscription-based and pay-per-view content access</li>
                <li>Creator channels and exclusive content</li>
                <li>User accounts with watchlist, history, and personalization features</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. Eligibility</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>You must be at least 13 years old to create an account</li>
                <li>Users under 18 must have parental or guardian consent</li>
                <li>You must provide accurate and complete registration information</li>
                <li>You are responsible for maintaining the confidentiality of your account</li>
                <li>Some content may have additional age restrictions (18+)</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. User Accounts</h2>
              <h3 className="text-xl font-medium text-white mb-3">4.1 Account Creation</h3>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>You may create an account using email/password or Google OAuth</li>
                <li>Each person may only maintain one account</li>
                <li>Account sharing is prohibited except within your household</li>
              </ul>
              
              <h3 className="text-xl font-medium text-white mb-3">4.2 Account Security</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>You are responsible for all activity under your account</li>
                <li>Notify us immediately if you suspect unauthorized access</li>
                <li>We may suspend accounts that show suspicious activity</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Subscriptions and Payments</h2>
              <h3 className="text-xl font-medium text-white mb-3">5.1 Subscription Plans</h3>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Free Tier:</strong> Limited content access with advertisements</li>
                <li><strong>Premium Subscriptions:</strong> Full access to content library, ad-free experience</li>
                <li><strong>Pay-Per-View:</strong> Individual video purchases</li>
              </ul>
              
              <h3 className="text-xl font-medium text-white mb-3">5.2 Billing</h3>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Subscriptions are billed in advance on a recurring basis (monthly/annually)</li>
                <li>Prices are displayed in GBP and may vary by region</li>
                <li>Payments are processed securely through Stripe</li>
                <li>You are responsible for any applicable taxes</li>
              </ul>
              
              <h3 className="text-xl font-medium text-white mb-3">5.3 Automatic Renewal</h3>
              <p className="mb-4">
                Subscriptions automatically renew unless cancelled before the renewal date. You can cancel at any time through your account settings. Access continues until the end of the current billing period.
              </p>
              
              <h3 className="text-xl font-medium text-white mb-3">5.4 Refunds</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Subscription refunds may be provided within 14 days of initial purchase (EU consumers)</li>
                <li>Pay-per-view purchases are generally non-refundable once viewing has begun</li>
                <li>Technical issues may qualify for refunds on a case-by-case basis</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Content and Licensing</h2>
              <h3 className="text-xl font-medium text-white mb-3">6.1 Content Ownership</h3>
              <p className="mb-4">
                All content on LTV VOD, including videos, graphics, logos, and text, is owned by LTV Production LTD, our content creators, or our licensors and is protected by copyright and intellectual property laws.
              </p>
              
              <h3 className="text-xl font-medium text-white mb-3">6.2 Limited License</h3>
              <p className="mb-4">
                We grant you a limited, non-exclusive, non-transferable license to access and view content for personal, non-commercial use only.
              </p>
              
              <h3 className="text-xl font-medium text-white mb-3">6.3 Prohibited Uses</h3>
              <p className="mb-4">You may NOT:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Download, copy, or record any streaming content</li>
                <li>Distribute, share, or publicly display content</li>
                <li>Modify, create derivative works, or reverse engineer content</li>
                <li>Use content for commercial purposes</li>
                <li>Remove or alter any copyright notices</li>
                <li>Use screen capture or recording software</li>
                <li>Circumvent any content protection or DRM measures</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Content Availability</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Content availability may vary by geographic location due to licensing restrictions</li>
                <li>Content may be added or removed from our library at any time</li>
                <li>We do not guarantee continuous availability of any specific content</li>
                <li>Previously purchased content may become unavailable if licensing expires</li>
                <li>Streaming quality may vary based on your internet connection and device</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Geographic Restrictions</h2>
              <p className="mb-4">
                Some content is subject to geographic licensing restrictions. You agree not to use VPNs, proxies, or other means to circumvent these restrictions. Violation may result in account termination.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Acceptable Use Policy</h2>
              <p className="mb-4">When using LTV VOD, you agree NOT to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Use the service for any illegal purpose</li>
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe on the rights of others</li>
                <li>Transmit viruses, malware, or harmful code</li>
                <li>Attempt unauthorized access to our systems</li>
                <li>Interfere with or disrupt our services</li>
                <li>Use automated systems (bots, scrapers) without permission</li>
                <li>Share your account credentials with others</li>
                <li>Engage in fraudulent payment activities</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. User-Generated Content</h2>
              <p className="mb-4">
                If our platform allows user-generated content (comments, reviews, etc.):
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>You retain ownership of content you submit</li>
                <li>You grant us a license to display and distribute your content</li>
                <li>You are responsible for ensuring your content doesn't violate others' rights</li>
                <li>We may remove content that violates these Terms</li>
                <li>We are not responsible for user-generated content</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">11. DMCA and Copyright</h2>
              <p className="mb-4">
                We respect intellectual property rights. If you believe content on our platform infringes your copyright, please submit a DMCA notice to:
              </p>
              <p className="mb-2">
                <a href="mailto:dmca@ltvproduction.com" className="text-purple-400 hover:underline">dmca@ltvproduction.com</a>
              </p>
              <p>
                See our full <Link to="/vod/dmca" className="text-purple-400 hover:underline">DMCA Policy</Link> for details.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">12. Service Modifications</h2>
              <p>
                We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time, with or without notice. This includes changing features, content, or pricing. We are not liable for any modification, suspension, or discontinuation.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">13. Disclaimers</h2>
              <p className="mb-4 uppercase text-sm">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
              </p>
              <p>
                We are not responsible for technical issues including but not limited to streaming quality, buffering, device compatibility, or service interruptions.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">14. Limitation of Liability</h2>
              <p className="uppercase text-sm">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, LTV PRODUCTION LTD SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE, INCLUDING BUT NOT LIMITED TO LOSS OF DATA, PROFITS, OR GOODWILL.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">15. Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless LTV Production LTD, its affiliates, and their respective officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of the Service or violation of these Terms.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">16. Termination</h2>
              <p className="mb-4">
                We may terminate or suspend your account immediately, without notice, for:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Violation of these Terms</li>
                <li>Fraudulent payment activity</li>
                <li>Abusive behavior toward staff or other users</li>
                <li>Any other reason at our sole discretion</li>
              </ul>
              <p className="mt-4">
                Upon termination, your right to use the Service will cease immediately. Sections that by their nature should survive termination will survive.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">17. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of England and Wales, without regard to conflict of law principles. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">18. Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. We will notify users of material changes via email or prominent notice on our platform. Your continued use of the Service after changes constitutes acceptance of the new Terms.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">19. Contact Us</h2>
              <p className="mb-4">For questions about these Terms of Service:</p>
              <div className="bg-gray-900/50 rounded-lg p-6 space-y-2">
                <p><strong>LTV Production LTD</strong></p>
                <p>Email: <a href="mailto:legal@ltvproduction.com" className="text-purple-400 hover:underline">legal@ltvproduction.com</a></p>
                <p>General Support: <a href="mailto:support@ltvproduction.com" className="text-purple-400 hover:underline">support@ltvproduction.com</a></p>
                <p>Location: London, United Kingdom</p>
              </div>
            </section>
          </div>
        </div>
      </main>
      
      <VODFooter />
    </div>
  );
};

export default VODTermsOfService;
